import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid, LineChart, Line } from 'recharts';
import { canAccessSymbol, SymbolName } from '../../lib/entitlements';
import '../Dashboard.css';

const API = process.env.REACT_APP_API_URL || 'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

const c = {
  bg: '#0f172a', card: '#1f2937', cardAlt: '#1a2332', border: '#374151',
  text: '#f9fafb', muted: '#9ca3af', dim: '#6b7280',
  green: '#16a34a', red: '#dc2626', blue: '#3b82f6', amber: '#d97706', purple: '#8b5cf6',
  cyan: '#06b6d4',
};

const fmt = (n: any, d = 2) => (n == null ? '—' : Number(n).toFixed(d));

const HORIZON_COLORS: Record<number, string> = {
  0: '#ef4444', 1: '#f97316', 7: '#f59e0b', 14: '#84cc16',
  21: '#22c55e', 30: '#10b981', 45: '#06b6d4', 60: '#3b82f6',
};

const SKEW_COLORS: Record<string, string> = {
  normal: '#16a34a', cautious: '#d97706', elevated: '#f97316', extreme: '#dc2626',
};

function getWeekRange(offsetWeeks = 0) {
  const now = new Date();
  const day = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1) + offsetWeeks * 7);
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  const toStr = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  return { start: toStr(monday), end: toStr(friday), label: `${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${friday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` };
}

const StatCard: React.FC<{ label: string; value: string; sub?: string; color?: string }> = ({ label, value, sub, color }) => (
  <div style={{ background: c.card, borderRadius: 8, border: `1px solid ${c.border}`, padding: '0.75rem', textAlign: 'center' }}>
    <div style={{ fontSize: '0.65rem', color: c.muted, marginBottom: '0.2rem' }}>{label}</div>
    <div style={{ fontSize: '1.2rem', fontWeight: 700, color: color || c.text }}>{value}</div>
    {sub && <div style={{ fontSize: '0.6rem', color: c.dim, marginTop: '0.15rem' }}>{sub}</div>}
  </div>
);

const SYMBOL_CONFIG: Record<string, { sym: SymbolName; multiplier: number; label: string; startingCapital: number }> = {
  SPY: { sym: 'SPY', multiplier: 100, label: 'SPY', startingCapital: 5000 },
  ES:  { sym: 'ES',  multiplier: 50,  label: 'ES',  startingCapital: 25000 },
  MES: { sym: 'MES', multiplier: 5,   label: 'MES', startingCapital: 10000 },
};

const IndexWeeklyReportInner: React.FC<{ symbol: string; title: string }> = ({ symbol, title }) => {
  const { user, token } = useAuth();
  const cfg = SYMBOL_CONFIG[symbol] || SYMBOL_CONFIG.SPY;
  const [signals, setSignals] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [aiSummary, setAiSummary] = useState<string>('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const week = getWeekRange(weekOffset);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setAiSummary('');
    try {
      const resp = await fetch(
        `${API}/indices/v2?history=true&symbol=${symbol}&startDate=${week.start}&endDate=${week.end}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await resp.json();
      setSignals((data.signals || []).sort((a: any, b: any) => a.snapshot_time.localeCompare(b.snapshot_time)));
    } catch { setSignals([]); }
    finally { setLoading(false); }

    setSummaryLoading(true);
    try {
      const resp = await fetch(
        `${API}/indices/v2-weekly-summary?symbol=${symbol}&start=${week.start}&end=${week.end}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await resp.json();
      setAiSummary(data.summary || '');
    } catch { /* non-fatal */ }
    finally { setSummaryLoading(false); }
  }, [token, week.start, week.end, symbol]);

  useEffect(() => { load(); }, [load]);

  if (!canAccessSymbol(user as any, cfg.sym)) {
    return (
      <div className="dashboard-container">
        <Navigation />
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '3rem', textAlign: 'center', color: c.text }}>
          <h2>{title} — Pro Feature</h2>
          <p style={{ color: c.muted, marginTop: 8 }}>Subscribe to access weekly performance reports.</p>
        </div>
      </div>
    );
  }

  // ── Compute metrics ──
  const spotPrices = signals.map(s => ({
    time: new Date(s.snapshot_time).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }),
    date: s.date || s.snapshot_time?.slice(0, 10),
    spot: Number(s.spot_price) || 0,
  }));

  const weekHigh = Math.max(...spotPrices.map(p => p.spot).filter(Boolean), 0);
  const weekLow = Math.min(...spotPrices.filter(p => p.spot > 0).map(p => p.spot), Infinity);
  const weekOpen = spotPrices[0]?.spot || 0;
  const weekClose = spotPrices[spotPrices.length - 1]?.spot || 0;
  const weekChange = weekOpen > 0 ? ((weekClose - weekOpen) / weekOpen) * 100 : 0;

  // Skew trend
  const skewData = signals.filter(s => s.skew).map(s => ({
    time: new Date(s.snapshot_time).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric' }),
    ratio: Number(s.skew?.skew_ratio) || 0,
    regime: s.skew?.skew_regime || 'normal',
  }));

  // Per-horizon credit by day
  const horizonsByDay: Record<string, Record<number, number>> = {};
  const horizonWins: Record<number, { total: number; matched: number }> = {};
  const allBestTrades: any[] = [];

  signals.forEach(s => {
    const day = s.date || s.snapshot_time?.slice(0, 10);
    const horizons = s.horizons || {};
    Object.entries(horizons).forEach(([dte, h]: [string, any]) => {
      const d = Number(dte);
      if (!horizonWins[d]) horizonWins[d] = { total: 0, matched: 0 };
      horizonWins[d].total++;
      if (h?.matched && h?.best) {
        horizonWins[d].matched++;
        if (!horizonsByDay[day]) horizonsByDay[day] = {};
        if (!horizonsByDay[day][d] || h.best.credit > horizonsByDay[day][d]) {
          horizonsByDay[day][d] = Number(h.best.credit) || 0;
        }
        allBestTrades.push({
          date: day,
          dte: d,
          strike: h.best.strike,
          credit: Number(h.best.credit) || 0,
          buffer: Number(h.best.points_from_atm) || 0,
          win: Number(h.best.prob_profit_pct) || 0,
          delta: Number(h.best.delta) || 0,
        });
      }
    });
  });

  // Daily horizon credit chart data
  const dailyDates = Array.from(new Set(signals.map(s => s.date || s.snapshot_time?.slice(0, 10)))).sort();
  const horizonChartData = dailyDates.map(day => {
    const row: any = { day: new Date(day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) };
    [0, 1, 7, 14, 21, 30, 45, 60].forEach(d => {
      row[`h${d}`] = horizonsByDay[day]?.[d] || 0;
    });
    return row;
  });

  // Best trade of the week
  const bestTrade = allBestTrades.length > 0
    ? allBestTrades.reduce((best, t) => t.credit > best.credit ? t : best, allBestTrades[0])
    : null;

  // Total signals, unique days
  const uniqueDays = Array.from(new Set(signals.map(s => s.date || s.snapshot_time?.slice(0, 10)))).length;
  const totalRuns = signals.length;

  // AI summary loaded from backend (see load function above)

  // Average skew
  const avgSkew = skewData.length > 0 ? skewData.reduce((a, b) => a + b.ratio, 0) / skewData.length : 0;
  const dominantRegime = skewData.length > 0
    ? (() => {
        const counts: Record<string, number> = {};
        skewData.forEach(s => { counts[s.regime] = (counts[s.regime] || 0) + 1; });
        return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
      })()
    : 'unknown';

  // ── Simulated $100K Portfolio ──
  // Rules: 10% of capital per trade ($10K), one trade per day (best 0 DTE during market hours),
  // Symbol multiplier = $100/point. Credit × 100 = premium collected per contract.
  // Max contracts = floor($10K / (strike × 100 margin)). For simplicity: 1 contract per trade.
  // Win = spot above strike at expiry (keep full credit). Loss = max(0, strike - SPX close) - credit.
  const STARTING_CAPITAL = cfg.startingCapital;
  const CAPITAL_PER_TRADE = 0.10; // 10%

  interface PortfolioTrade {
    date: string;
    dte: number;
    strike: number;
    credit: number;
    expiration: string;
    spotAtEntry: number;
    status: 'win' | 'loss' | 'open';
    pnl: number;
    spotAtExpiry?: number;
  }

  const portfolioTrades: PortfolioTrade[] = [];
  const tradesByDate: Record<string, boolean> = {}; // one trade per day

  // Sort signals chronologically
  const chronoSignals = [...signals].sort((a, b) => a.snapshot_time.localeCompare(b.snapshot_time));

  // Build a map of close prices by date (last spot price of the day)
  const spotByDate: Record<string, number> = {};
  chronoSignals.forEach(s => {
    const d = s.date || s.snapshot_time?.slice(0, 10);
    spotByDate[d] = Number(s.spot_price) || spotByDate[d] || 0;
  });

  // For each signal run, pick one trade per day — prefer 0 DTE (expired same day) for definitive P&L
  chronoSignals.forEach(s => {
    const day = s.date || s.snapshot_time?.slice(0, 10);
    if (tradesByDate[day]) return; // one trade per day

    const horizons = s.horizons || {};
    // Try 0 DTE first, then 1, then 7 etc.
    for (const dte of [0, 1, 7, 14, 21, 30, 45, 60]) {
      const h = horizons[String(dte)];
      if (!h?.matched || !h?.best) continue;

      const strike = Number(h.best.strike);
      const credit = Number(h.best.credit);
      const expiration = h.best.expiration || h.expiration || '';
      const spotAtEntry = Number(s.spot_price) || 0;

      if (strike <= 0 || credit <= 0) continue;

      // Determine outcome
      let status: 'win' | 'loss' | 'open' = 'open';
      let pnl = 0;
      let spotAtExpiry: number | undefined;

      // Check if expiration date has passed (we have spot data for it)
      const expDate = expiration.slice(0, 10);
      if (spotByDate[expDate] && expDate <= day) {
        // Expired same day or earlier — use last known spot for that date
        spotAtExpiry = spotByDate[expDate];
        if (spotAtExpiry >= strike) {
          status = 'win';
          pnl = credit * cfg.multiplier; // Keep full premium
        } else {
          status = 'loss';
          pnl = -((strike - spotAtExpiry) * cfg.multiplier) + (credit * cfg.multiplier);
        }
      } else if (spotByDate[expDate]) {
        // Expiration date has data — check outcome
        spotAtExpiry = spotByDate[expDate];
        if (spotAtExpiry >= strike) {
          status = 'win';
          pnl = credit * cfg.multiplier;
        } else {
          status = 'loss';
          pnl = -((strike - spotAtExpiry) * cfg.multiplier) + (credit * cfg.multiplier);
        }
      } else {
        // Still open — estimate P&L using latest spot and buffer
        const latestSpot = weekClose || spotAtEntry;
        if (latestSpot >= strike) {
          pnl = credit * cfg.multiplier * 0.5; // Estimate: partial theta decay
        } else {
          pnl = -((strike - latestSpot) * cfg.multiplier) + (credit * cfg.multiplier);
        }
        status = 'open';
      }

      portfolioTrades.push({ date: day, dte, strike, credit, expiration, spotAtEntry, status, pnl, spotAtExpiry });
      tradesByDate[day] = true;
      break; // one trade per day
    }
  });

  // Build equity curve
  let runningPnl = 0;
  const equityCurve = portfolioTrades.map(t => {
    runningPnl += t.pnl;
    return {
      date: new Date(t.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      equity: STARTING_CAPITAL + runningPnl,
      pnl: runningPnl,
      tradePnl: t.pnl,
    };
  });

  const totalPnl = portfolioTrades.reduce((sum, t) => sum + t.pnl, 0);
  const wins = portfolioTrades.filter(t => t.status === 'win').length;
  const losses = portfolioTrades.filter(t => t.status === 'loss').length;
  const opens = portfolioTrades.filter(t => t.status === 'open').length;
  const winRate = (wins + losses) > 0 ? (wins / (wins + losses)) * 100 : 0;
  const totalPremium = portfolioTrades.reduce((sum, t) => sum + t.credit * cfg.multiplier, 0);
  const maxDrawdown = equityCurve.length > 0
    ? Math.min(...equityCurve.map(e => e.pnl)) : 0;
  const returnOnCapital = (totalPnl / STARTING_CAPITAL) * 100;

  return (
    <div className="dashboard-container">
      <Navigation />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '1.5rem', color: c.text, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.4rem' }}>{title}</h1>
            <div style={{ color: c.muted, fontSize: '0.85rem' }}>{week.label}</div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={() => setWeekOffset(w => w - 1)} style={{
              padding: '0.4rem 0.75rem', borderRadius: 6, border: `1px solid ${c.border}`,
              background: 'transparent', color: c.muted, cursor: 'pointer', fontSize: '0.8rem',
            }}>Previous Week</button>
            {weekOffset < 0 && (
              <button onClick={() => setWeekOffset(w => w + 1)} style={{
                padding: '0.4rem 0.75rem', borderRadius: 6, border: `1px solid ${c.border}`,
                background: 'transparent', color: c.muted, cursor: 'pointer', fontSize: '0.8rem',
              }}>Next Week</button>
            )}
            {weekOffset !== 0 && (
              <button onClick={() => setWeekOffset(0)} style={{
                padding: '0.4rem 0.75rem', borderRadius: 6, border: 'none',
                background: c.purple, color: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
              }}>This Week</button>
            )}
            {signals.length > 0 && !summaryLoading && (
              <button
                disabled={pdfLoading}
                onClick={async () => {
                  if (!token) return;
                  setPdfLoading(true);
                  try {
                    const sections: { heading: string; body: string }[] = [];
                    if (aiSummary) {
                      sections.push({ heading: 'AI Weekly Analysis', body: aiSummary.replace(/\*\*/g, '').replace(/^#+\s*/gm, '') });
                    }
                    sections.push({ heading: 'Portfolio Simulation', body: `Starting capital: $${STARTING_CAPITAL.toLocaleString()}. ${portfolioTrades.length} trades taken. ${wins} wins, ${losses} losses, ${opens} open. Win rate: ${winRate.toFixed(0)}%. Total P&L: $${totalPnl.toLocaleString()}. Return: ${returnOnCapital.toFixed(2)}%. Max drawdown: $${maxDrawdown.toLocaleString()}.` });
                    const topTrades = [...allBestTrades].sort((a, b) => b.credit - a.credit).slice(0, 10);
                    const resp = await fetch(`${API}/pdf/generate`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        title: `${symbol} Weekly Performance Report`,
                        subtitle: week.label,
                        filename: `${symbol.toLowerCase()}-weekly-report-${week.start}`,
                        stats: [
                          { label: 'Open', value: `$${weekOpen.toFixed(0)}` },
                          { label: 'Close', value: `$${weekClose.toFixed(0)}` },
                          { label: 'Change', value: `${weekChange >= 0 ? '+' : ''}${weekChange.toFixed(2)}%` },
                          { label: 'Win Rate', value: `${winRate.toFixed(0)}%` },
                          { label: 'P&L', value: `$${totalPnl.toLocaleString()}` },
                        ],
                        sections,
                        tables: topTrades.length > 0 ? [{
                          title: 'Top 10 Trades by Credit',
                          headers: ['Date', 'DTE', 'Strike', 'Credit', 'Buffer', 'Win%', 'Delta'],
                          rows: topTrades.map(t => [t.date, `${t.dte}`, `${t.strike}`, `$${t.credit.toFixed(2)}`, `${t.buffer.toFixed(0)}pts`, `${t.win.toFixed(1)}%`, t.delta.toFixed(3)]),
                        }] : [],
                        footer: 'Simulated results — not actual trades. For educational purposes only. Not financial advice. Wealth Financial Systems LLC.',
                      }),
                    });
                    const data = await resp.json();
                    if (data.url) window.open(data.url, '_blank');
                  } catch (err: any) { console.error('PDF error:', err.message); }
                  finally { setPdfLoading(false); }
                }}
                style={{
                  padding: '0.4rem 0.75rem', borderRadius: 6, border: `1px solid ${c.border}`,
                  background: 'transparent', color: pdfLoading ? c.dim : c.muted, cursor: pdfLoading ? 'wait' : 'pointer', fontSize: '0.8rem',
                }}
              >{pdfLoading ? 'Generating...' : 'Download PDF'}</button>
            )}
          </div>
        </div>

        {loading && <div style={{ textAlign: 'center', padding: '3rem', color: c.muted }}>Loading week data...</div>}

        {!loading && signals.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem', color: c.muted }}>No research found for this week.</div>
        )}

        {!loading && signals.length > 0 && (
          <>
            {/* AI Weekly Summary — at top */}
            <div style={{ background: c.card, borderRadius: 8, border: `1px solid ${c.blue}40`, borderLeft: `4px solid ${c.blue}`, padding: '1rem', marginBottom: '1rem' }}>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', color: c.blue }}>AI Weekly Analysis</h3>
              {summaryLoading && <div style={{ color: c.muted, fontSize: '0.85rem' }}>Generating weekly analysis...</div>}
              {!summaryLoading && aiSummary && (
                <div style={{ fontSize: '0.85rem', color: c.muted, lineHeight: 1.7, whiteSpace: 'pre-line' }}>{aiSummary}</div>
              )}
              {!summaryLoading && !aiSummary && (
                <div style={{ color: c.dim, fontSize: '0.85rem' }}>No analysis available for this week.</div>
              )}
            </div>

            {/* Summary stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <StatCard label="Week Open" value={`${fmt(weekOpen, 0)}`} />
              <StatCard label="Week Close" value={`${fmt(weekClose, 0)}`} />
              <StatCard label="Week Change" value={`${weekChange > 0 ? '+' : ''}${fmt(weekChange, 2)}%`} color={weekChange >= 0 ? c.green : c.red} />
              <StatCard label="Week Range" value={`${fmt(weekHigh - weekLow, 0)}pts`} sub={`${fmt(weekLow, 0)} - ${fmt(weekHigh, 0)}`} />
              <StatCard label="Research Runs" value={String(totalRuns)} sub={`${uniqueDays} trading days`} />
              <StatCard label="Avg Fear" value={fmt(avgSkew, 4)} color={SKEW_COLORS[dominantRegime] || c.text} sub={dominantRegime.charAt(0).toUpperCase() + dominantRegime.slice(1)} />
              {bestTrade && (
                <StatCard label="Best Trade" value={`$${fmt(bestTrade.credit, 2)}`} color={c.green}
                  sub={`${bestTrade.dte}d strike ${bestTrade.strike}`} />
              )}
            </div>
            <div style={{ fontSize: '0.6rem', color: c.dim, marginBottom: '1rem', fontStyle: 'italic' }}>
              Weekly snapshot of {symbol} put-selling research. Open/close from first and last research runs. Fear gauge averaged across all runs.
            </div>

            {/* Simulated Portfolio */}
            {portfolioTrades.length > 0 && (
              <div style={{ background: c.card, borderRadius: 8, border: `1px solid ${c.purple}30`, padding: '1rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.1rem' }}>
                  <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Simulated ${STARTING_CAPITAL.toLocaleString()} Portfolio</h3>
                  <span style={{ fontSize: '0.6rem', padding: '0.1rem 0.4rem', borderRadius: 10, background: c.purple + '25', color: c.purple, fontWeight: 600 }}>SIMULATION</span>
                </div>
                <div style={{ fontSize: '0.65rem', color: c.dim, marginBottom: '0.75rem' }}>
                  Hypothetical results if you traded our top pick each day with ${STARTING_CAPITAL.toLocaleString()} capital (10% per trade, 1 contract, {symbol} ${cfg.multiplier} multiplier). Not financial advice.
                </div>

                {/* Portfolio stat cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <StatCard label="Starting Capital" value={`$${STARTING_CAPITAL.toLocaleString()}`} />
                  <StatCard label="Ending Capital" value={`$${(STARTING_CAPITAL + totalPnl).toLocaleString('en-US', { maximumFractionDigits: 0 })}`} color={totalPnl >= 0 ? c.green : c.red} />
                  <StatCard label="Net P&L" value={`${totalPnl >= 0 ? '+' : ''}$${totalPnl.toLocaleString('en-US', { maximumFractionDigits: 0 })}`} color={totalPnl >= 0 ? c.green : c.red} />
                  <StatCard label="Return" value={`${returnOnCapital >= 0 ? '+' : ''}${fmt(returnOnCapital, 2)}%`} color={returnOnCapital >= 0 ? c.green : c.red} sub={`on $${STARTING_CAPITAL.toLocaleString()}`} />
                  <StatCard label="Trades" value={String(portfolioTrades.length)} sub={`${wins}W / ${losses}L / ${opens}O`} />
                  <StatCard label="Win Rate" value={`${fmt(winRate, 0)}%`} color={winRate >= 70 ? c.green : winRate >= 50 ? c.amber : c.red} sub="closed trades only" />
                  <StatCard label="Premium Collected" value={`$${totalPremium.toLocaleString('en-US', { maximumFractionDigits: 0 })}`} color={c.cyan} sub="gross before losses" />
                  <StatCard label="Max Drawdown" value={maxDrawdown < 0 ? `-$${Math.abs(maxDrawdown).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '$0'} color={maxDrawdown < 0 ? c.red : c.green} />
                </div>

                {/* Equity Curve */}
                {equityCurve.length >= 2 && (
                  <>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.3rem' }}>Equity Curve</div>
                    <ResponsiveContainer width="100%" height={180}>
                      <AreaChart data={equityCurve} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                        <defs>
                          <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={totalPnl >= 0 ? c.green : c.red} stopOpacity={0.3} />
                            <stop offset="95%" stopColor={totalPnl >= 0 ? c.green : c.red} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={c.border + '40'} />
                        <XAxis dataKey="date" tick={{ fill: c.dim, fontSize: 10 }} axisLine={{ stroke: c.border }} />
                        <YAxis domain={['auto', 'auto']} tick={{ fill: c.dim, fontSize: 10 }} axisLine={{ stroke: c.border }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}K`} />
                        <Tooltip contentStyle={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 6, fontSize: '0.8rem' }} formatter={(v: any) => [`$${Number(v).toLocaleString()}`, 'Equity']} />
                        <ReferenceLine y={STARTING_CAPITAL} stroke={c.muted} strokeDasharray="3 3" />
                        <Area type="monotone" dataKey="equity" stroke={totalPnl >= 0 ? c.green : c.red} fill="url(#eqGrad)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </>
                )}

                {/* Trade log */}
                <div style={{ fontSize: '0.75rem', fontWeight: 600, marginTop: '0.75rem', marginBottom: '0.3rem' }}>Trade Log</div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <thead>
                      <tr>
                        {['Date', 'DTE', 'Strike', 'Credit', `${symbol} at Entry`, `${symbol} at Expiry`, 'Status', 'P&L'].map(h => (
                          <th key={h} style={{ padding: '0.4rem', textAlign: 'left', borderBottom: `1px solid ${c.border}`, color: c.muted, fontSize: '0.65rem', fontWeight: 600 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {portfolioTrades.map((t, i) => (
                        <tr key={i}>
                          <td style={{ padding: '0.35rem 0.4rem', borderBottom: `1px solid ${c.border}22` }}>{t.date}</td>
                          <td style={{ padding: '0.35rem 0.4rem', borderBottom: `1px solid ${c.border}22`, color: HORIZON_COLORS[t.dte] }}>{t.dte}d</td>
                          <td style={{ padding: '0.35rem 0.4rem', borderBottom: `1px solid ${c.border}22`, fontWeight: 600 }}>{t.strike}</td>
                          <td style={{ padding: '0.35rem 0.4rem', borderBottom: `1px solid ${c.border}22` }}>${fmt(t.credit, 2)}</td>
                          <td style={{ padding: '0.35rem 0.4rem', borderBottom: `1px solid ${c.border}22` }}>{fmt(t.spotAtEntry, 2)}</td>
                          <td style={{ padding: '0.35rem 0.4rem', borderBottom: `1px solid ${c.border}22` }}>{t.spotAtExpiry ? fmt(t.spotAtExpiry, 2) : '—'}</td>
                          <td style={{ padding: '0.35rem 0.4rem', borderBottom: `1px solid ${c.border}22` }}>
                            <span style={{
                              padding: '0.1rem 0.4rem', borderRadius: 10, fontSize: '0.65rem', fontWeight: 600,
                              background: t.status === 'win' ? c.green + '20' : t.status === 'loss' ? c.red + '20' : c.amber + '20',
                              color: t.status === 'win' ? c.green : t.status === 'loss' ? c.red : c.amber,
                            }}>{t.status.toUpperCase()}</span>
                          </td>
                          <td style={{
                            padding: '0.35rem 0.4rem', borderBottom: `1px solid ${c.border}22`,
                            fontWeight: 700, color: t.pnl >= 0 ? c.green : c.red,
                          }}>{t.pnl >= 0 ? '+' : ''}${t.pnl.toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Price Chart */}
            <div style={{ background: c.card, borderRadius: 8, border: `1px solid ${c.border}`, padding: '1rem', marginBottom: '1rem' }}>
              <h3 style={{ margin: '0 0 0.1rem', fontSize: '0.95rem' }}>{symbol} Price Movement</h3>
              <div style={{ fontSize: '0.65rem', color: c.dim, marginBottom: '0.5rem' }}>{symbol} spot price at each research run through the week. Dashed line = week's opening price.</div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={spotPrices} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <defs>
                    <linearGradient id="spotGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={weekChange >= 0 ? c.green : c.red} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={weekChange >= 0 ? c.green : c.red} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={c.border + '40'} />
                  <XAxis dataKey="time" tick={{ fill: c.dim, fontSize: 10 }} axisLine={{ stroke: c.border }} interval={Math.max(0, Math.floor(spotPrices.length / 6))} />
                  <YAxis domain={['auto', 'auto']} tick={{ fill: c.dim, fontSize: 10 }} axisLine={{ stroke: c.border }} />
                  <Tooltip contentStyle={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 6, fontSize: '0.8rem' }} />
                  <ReferenceLine y={weekOpen} stroke={c.muted} strokeDasharray="3 3" label={{ value: 'Week Open', fill: c.dim, fontSize: 10 }} />
                  <Area type="monotone" dataKey="spot" stroke={weekChange >= 0 ? c.green : c.red} fill="url(#spotGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Skew Trend */}
            {skewData.length > 0 && (
              <div style={{ background: c.card, borderRadius: 8, border: `1px solid ${c.border}`, padding: '1rem', marginBottom: '1rem' }}>
                <h3 style={{ margin: '0 0 0.1rem', fontSize: '0.95rem' }}>Market Fear Gauge Trend</h3>
              <div style={{ fontSize: '0.65rem', color: c.dim, marginBottom: '0.5rem' }}>Tracks how fearful the market is. Higher = more demand for downside protection. Dashed lines mark regime boundaries.</div>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={skewData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={c.border + '40'} />
                    <XAxis dataKey="time" tick={{ fill: c.dim, fontSize: 10 }} axisLine={{ stroke: c.border }} interval={Math.max(0, Math.floor(skewData.length / 6))} />
                    <YAxis domain={[1.0, 'auto']} tick={{ fill: c.dim, fontSize: 10 }} axisLine={{ stroke: c.border }} />
                    <Tooltip contentStyle={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 6, fontSize: '0.8rem' }} />
                    <ReferenceLine y={1.15} stroke={c.amber} strokeDasharray="3 3" label={{ value: 'Cautious', fill: c.amber, fontSize: 9 }} />
                    <ReferenceLine y={1.25} stroke="#f97316" strokeDasharray="3 3" label={{ value: 'Elevated', fill: '#f97316', fontSize: 9 }} />
                    <ReferenceLine y={1.40} stroke={c.red} strokeDasharray="3 3" label={{ value: 'Extreme', fill: c.red, fontSize: 9 }} />
                    <Line type="monotone" dataKey="ratio" stroke={c.purple} strokeWidth={2} dot={{ r: 2, fill: c.purple }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Daily Credit by Horizon */}
            {horizonChartData.length > 0 && (
              <div style={{ background: c.card, borderRadius: 8, border: `1px solid ${c.border}`, padding: '1rem', marginBottom: '1rem' }}>
                <h3 style={{ margin: '0 0 0.1rem', fontSize: '0.95rem' }}>Best Daily Credit by Horizon</h3>
                <div style={{ fontSize: '0.65rem', color: c.dim, marginBottom: '0.5rem' }}>The highest credit available at each time horizon per day. Taller bars = more premium to collect (often means higher volatility).</div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={horizonChartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={c.border + '40'} />
                    <XAxis dataKey="day" tick={{ fill: c.dim, fontSize: 10 }} axisLine={{ stroke: c.border }} />
                    <YAxis tick={{ fill: c.dim, fontSize: 10 }} axisLine={{ stroke: c.border }} />
                    <Tooltip contentStyle={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 6, fontSize: '0.8rem' }} />
                    {[7, 14, 21, 30, 45, 60].map(d => (
                      <Bar key={d} dataKey={`h${d}`} name={`${d}d`} fill={HORIZON_COLORS[d]} stackId="credit" />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                  {[7, 14, 21, 30, 45, 60].map(d => (
                    <span key={d} style={{ fontSize: '0.7rem', color: HORIZON_COLORS[d], display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <span style={{ width: 8, height: 8, background: HORIZON_COLORS[d], borderRadius: 2, display: 'inline-block' }} />
                      {d}d
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Horizon Availability */}
            <div style={{ background: c.card, borderRadius: 8, border: `1px solid ${c.border}`, padding: '1rem', marginBottom: '1rem' }}>
              <h3 style={{ margin: '0 0 0.1rem', fontSize: '0.95rem' }}>Horizon Availability</h3>
              <div style={{ fontSize: '0.65rem', color: c.dim, marginBottom: '0.5rem' }}>Percentage of research runs that had valid candidates for each time horizon. Short-dated (0-1 DTE) are often unavailable outside market hours.</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '0.5rem' }}>
                {[0, 1, 7, 14, 21, 30, 45, 60].map(d => {
                  const hw = horizonWins[d] || { total: 0, matched: 0 };
                  const pct = hw.total > 0 ? Math.round((hw.matched / hw.total) * 100) : 0;
                  return (
                    <div key={d} style={{ textAlign: 'center', padding: '0.5rem', borderRadius: 6, background: c.cardAlt }}>
                      <div style={{ fontSize: '0.7rem', color: c.muted }}>{d} DTE</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 700, color: HORIZON_COLORS[d] }}>{pct}%</div>
                      <div style={{ fontSize: '0.6rem', color: c.dim }}>{hw.matched}/{hw.total} runs</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top Trades Table */}
            {allBestTrades.length > 0 && (
              <div style={{ background: c.card, borderRadius: 8, border: `1px solid ${c.border}`, padding: '1rem', marginBottom: '1rem' }}>
                <h3 style={{ margin: '0 0 0.1rem', fontSize: '0.95rem' }}>Top Trades This Week</h3>
                <div style={{ fontSize: '0.65rem', color: c.dim, marginBottom: '0.5rem' }}>The 15 highest-credit put-selling opportunities identified this week, ranked by premium collected.</div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr>
                        {['#', 'Date', 'Horizon', 'Strike', 'Credit', 'Buffer', 'Win %', 'Delta'].map(h => (
                          <th key={h} style={{ padding: '0.5rem', textAlign: 'left', borderBottom: `1px solid ${c.border}`, color: c.muted, fontSize: '0.7rem', fontWeight: 600 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allBestTrades
                        .sort((a, b) => b.credit - a.credit)
                        .slice(0, 15)
                        .map((t, i) => (
                          <tr key={i} style={{
                            background: i === 0 ? c.green + '25' : i < 3 ? c.green + '08' : 'transparent',
                            borderLeft: i === 0 ? `3px solid ${c.green}` : '3px solid transparent',
                          }}>
                            <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${c.border}22`, fontWeight: i === 0 ? 700 : 400, color: i === 0 ? c.green : c.dim }}>{i + 1}</td>
                            <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${c.border}22` }}>{t.date}</td>
                            <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${c.border}22`, color: HORIZON_COLORS[t.dte] }}>{t.dte}d</td>
                            <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${c.border}22`, fontWeight: 600 }}>{t.strike}</td>
                            <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${c.border}22`, color: c.green, fontWeight: 700 }}>${fmt(t.credit, 2)}</td>
                            <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${c.border}22` }}>{fmt(t.buffer, 0)}pts</td>
                            <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${c.border}22` }}>{fmt(t.win, 1)}%</td>
                            <td style={{ padding: '0.4rem 0.5rem', borderBottom: `1px solid ${c.border}22` }}>{fmt(t.delta, 3)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* AI summary moved to top of page */}
          </>
        )}
      </div>
    </div>
  );
};

export const SpyWeeklyReport: React.FC = () => <IndexWeeklyReportInner symbol="SPY" title="SPY Weekly Performance Report" />;
export const EsWeeklyReport: React.FC = () => <IndexWeeklyReportInner symbol="ES" title="ES Weekly Performance Report" />;
export const MesWeeklyReport: React.FC = () => <IndexWeeklyReportInner symbol="MES" title="MES Weekly Performance Report" />;
